import { useState, useEffect } from "react";
import { useParams, useLocation } from "react-router-dom";
import {
    TextField,
    Modal,
    Box,
    CircularProgress,
    Select,
    MenuItem,
} from "@mui/material";
import cogoToast from "cogo-toast";
import "../assets/stylesheets/bootstrap.min.css";
import "../assets/stylesheets/style.css";
import "../assets/stylesheets/responsive.css";
import TestimonialsCarousel from "../components/leadMagnet/TestimonialCarousal";
import Faq from "../components/Faq";
import bg0 from "../assets/images/leadMagnet/bg0.svg";
import bg1 from "../assets/images/leadMagnet/bg1.svg";
import bg2 from "../assets/images/leadMagnet/bg2.svg";
import blueBack from "../assets/images/leadMagnet/blue-back.png";
import {
    getLeadMagnet,
    postLeadMagnetResponseForm,
    downloadLeadMagnet,
} from "../api/utils";
import useFetch from "../api/hooks/useFetch";
import { images } from "../constants/images";
import auditReportPdf from "../assets/audit_report_1.pdf";
import { Helmet } from "react-helmet-async";

// Modal styles
const modalStyle = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: { xs: "90%", sm: "500px" },
    bgcolor: "background.paper",
    borderRadius: "12px",
    boxShadow: 24,
    p: 4,
    outline: "none",
};

// Hero Section Component
const HeroSection = ({
    content,
    offeringType,
    formData,
    handleInputChange,
    showError,
    isSubmitting,
    submitLeadForm,
    downloadLink,
    handleManualDownload,
    isFormSubmitted,
    nicheTypes = [], // Add default value
}) => (
    <section
        className="page-hero-sec"
        style={{
            backgroundImage: `linear-gradient(to right, rgba(11, 29, 81, 0.7), rgba(11, 29, 81, 0.7)), url(${blueBack})`,
            backgroundSize: "100% 100%",
            backgroundRepeat: "no-repeat",
            minHeight: "80vh",
            display: "flex",
            alignItems: "center",
        }}
    >
        <div className="container mb-5">
            <div className="row align-items-center">
                <div className="col-lg-7 col-md-12">
                    <div className="hero-content text-white">
                        <h1
                            className="head-font mb-4"
                            style={{
                                fontSize: "3.5rem",
                                fontWeight: "bold",
                                lineHeight: "1.2",
                            }}
                        >
                            How an Audit Keeps Your Timeline on Track
                        </h1>
                        <p
                            className="fs-17 mb-4 text-warning"
                            style={{ lineHeight: "1.6", fontWeight: "bold" }}
                        >
                            Tired of chasing your developers for updates? This free guide shows how a Software Audit keeps your project timeline on track — from Day 1.
                        </p>
                        <br />
                        <p
                            className="fs-17 mb-4 text-white"
                            style={{ lineHeight: "1.6" }}
                        >
                            Every founder starts with a confident 4-week promise.
                            Then it becomes 8. Then 12. Then “just one more sprint.”

                            It’s not bad luck, it’s bad planning.
                            This free eBook reveals how an early Software Audit fixes that mess before it starts.


                        </p>
                    </div>
                </div>
                <div className="col-lg-5 col-md-12">
                    <div
                        className="hero-form bg-white p-4 rounded shadow-lg"
                        style={{ maxWidth: "400px", margin: "0 auto" }}
                    >
                        <div className="text-center mb-4">
                            <h5 className="tc-primary head-font mb-2">
                                Get the E-Book Now
                            </h5>
                            <p className="text-muted small">
                                Enter your details to download instantly
                            </p>
                        </div>

                        



                      
                        {(!isFormSubmitted ||
                            (offeringType === "E-Book")) && (
                                <>
                                    <div className="mb-4">
                                        <TextField
                                            fullWidth
                                            label="Full Name"
                                            name="name"
                                            value={formData.name}
                                            onChange={handleInputChange}
                                            error={showError && !formData.name.trim()}
                                            helperText={
                                                showError && !formData.name.trim()
                                                    ? "Name is required"
                                                    : ""
                                            }
                                            sx={{ backgroundColor: "#f8fafc" }}
                                        />
                                    </div>
                                    <div className="mb-4">
                                        <TextField
                                            fullWidth
                                            label="Email Address"
                                            name="email"
                                            type="email"
                                            value={formData.email}
                                            onChange={handleInputChange}
                                            error={showError && !formData.email.trim()}
                                            helperText={
                                                showError && !formData.email.trim()
                                                    ? "Valid email is required"
                                                    : ""
                                            }
                                            sx={{ backgroundColor: "#f8fafc" }}
                                        />
                                    </div>
                                    <div className="mb-4">
                                        <TextField
                                            fullWidth
                                            label="Phone Number"
                                            name="phone"
                                            type="text"
                                            value={formData.phone}
                                            onChange={handleInputChange}
                                            error={showError && !formData.phone.trim()}
                                            helperText={
                                                showError && !formData.phone.trim()
                                                    ? "Valid phone is required"
                                                    : ""
                                            }
                                            inputProps={{ maxLength: 10 }}
                                            sx={{ backgroundColor: "#f8fafc" }}
                                        />
                                    </div>

                                    <div className="mb-4">
                                        <Select
                                            fullWidth
                                            name="nicheType"
                                            value={formData.nicheType}
                                            onChange={handleInputChange}
                                            displayEmpty
                                            error={showError && !formData.nicheType}
                                            sx={{
                                                "& .MuiSelect-select": {
                                                    background: "#e0e5ef",
                                                    padding: "12px 14px",
                                                },
                                                "& .MuiOutlinedInput-root": {
                                                    "& fieldset": {
                                                        borderColor: "#e0e5ef",
                                                    },
                                                    "&:hover fieldset": {
                                                        borderColor: "#b0b5bf",
                                                    },
                                                    "&.Mui-focused fieldset": {
                                                        borderColor: "#3f51b5",
                                                    },
                                                },
                                            }}
                                            inputProps={{
                                                "aria-label": "Industry Type",
                                            }}
                                        >
                                            <MenuItem value="" disabled>
                                                Industry Type
                                            </MenuItem>
                                            {nicheTypes?.map((type) => (
                                                <MenuItem key={type} value={type}>
                                                    {type}
                                                </MenuItem>
                                            ))}
                                        </Select>

                                        {showError && !formData.nicheType && (
                                            <p
                                                style={{
                                                    color: "#d32f2f",
                                                    fontSize: "0.75rem",
                                                    margin: "3px 14px 0",
                                                }}
                                            >
                                                Industry type is required
                                            </p>
                                        )}
                                    </div>

                                    {formData.nicheType === "Other" && (
                                        <div className="mb-4">
                                            <TextField
                                                fullWidth
                                                variant="outlined"
                                                label="Specify Your Industry"
                                                name="industry_other"
                                                value={formData.industry_other}
                                                onChange={handleInputChange}
                                                error={
                                                    showError &&
                                                    formData.nicheType === "Other" &&
                                                    !formData.industry_other.trim()
                                                }
                                                helperText={
                                                    showError &&
                                                        formData.nicheType === "Other" &&
                                                        !formData.industry_other.trim()
                                                        ? "Please specify your industry"
                                                        : ""
                                                }
                                                required={formData.nicheType === "Other"}
                                                margin="normal"
                                                inputProps={{ maxLength: 100 }}
                                                sx={{
                                                    "& .MuiOutlinedInput-root": {
                                                        "& input": {
                                                            background: "#e0e5ef",
                                                            padding: "12px 14px",
                                                        },
                                                    },
                                                }}
                                            />
                                        </div>
                                    )}

                                    <button
                                        onClick={submitLeadForm}
                                        className="LM_link_btn1"
                                        disabled={isSubmitting}
                                        style={{
                                            background: "#0a2458",
                                            border: "none",
                                            padding: "12px",
                                            fontWeight: "600",
                                        }}
                                    >
                                        {isSubmitting ? (
                                            <>
                                                <CircularProgress
                                                    size={20}
                                                    sx={{ color: "white", mr: 1 }}
                                                />
                                                Submitting...
                                            </>
                                        ) : (
                                            "Get Your Free E-Book"
                                        )}
                                    </button>

                                    <p className="text-center text-muted small mt-2 mb-0">
                                        We respect your privacy. Unsubscribe at any time.
                                    </p>
                                </>
                            )}
                    </div>
                </div>
            </div>
        </div>
    </section>
);


const ThingsYouWillGetInThisReport = ({ openModal }) => {

    return (
        <section
            className="d-flex align-items-center justify-content-center min-vauto"
            style={{
                backgroundImage: `url(${bg0})`,
                backgroundPosition: "center",
                backgroundSize: "cover",
                backgroundRepeat: "no-repeat",
            }}
        >
            <div className="container mt-5">
                <div className="row text-center mb-5 justify-content-center">
                    {/* Left Side */}
                    <div className="col-lg-12">
                        <h2 className="tc-primary text-center head-font mb-4">
                            What You’ll Learn Inside?
                        </h2>

                        <ul
                            className="text-center"
                            style={{
                                listStyle: "none",
                                fontFamily: "Inter",
                                fontStyle: "normal",
                                fontWeight: 400,
                                fontSize: "20px",
                                lineHeight: "32px",
                                color: "#000000",
                            }}
                        >
                            <li>✅ The real reason most software projects miss deadlines.</li>
                            <li>✅ How a Software Audit pre-plans your timeline before a single line of code.</li>
                            <li>✅ What a milestone-based roadmap actually looks like (with examples).</li>
                            <li>✅ How to avoid “ghost delays” and finally get real visibility.</li>
                            <li>✅ A simple weekly rhythm that keeps your team accountable (without micromanaging).</li>
                        </ul>




                        <button onClick={openModal} className="LM_link_btn">
                            Get Your Free E-Book
                        </button>


                    </div>

                </div>
            </div>

        </section>

    );
};

const WhoIsThisFor = ({ openModal }) => (
    <section
        className="d-flex align-items-center justify-content-center min-vauto"
        style={{
            backgroundImage: `url(${bg1})`,
            backgroundPosition: "center",
            backgroundSize: "cover",
            backgroundRepeat: "no-repeat",
        }}
    >
        <div className="container mt-1 mb-5">
            <div className="row justify-content-center mt-5">
                <div className="col-lg-6">
                    <img
                        src={images.ebook2}
                        alt="Banner"
                        className="top-0 start-0 w-100 auto"
                        style={{ objectFit: "cover" }}
                    />
                </div>
                <div className="col-lg-6 align-self-center">
                    <h2 className="tc-primary text-center head-font mb-4">
                        Who This Is For:

                    </h2>

                    <p
                        className="py-5 text-center"
                        style={{
                            fontFamily: "Inter",
                            fontStyle: "normal",
                            fontWeight: 400,
                            fontSize: "24px",
                            lineHeight: "36px",
                            color: "#000000",
                        }}
                    >
                        <ul
                            className="text-center"
                            style={{
                                listStyle: "none",
                                fontFamily: "Inter",
                                fontStyle: "normal",
                                fontWeight: 400,
                                fontSize: "24px",
                                lineHeight: "36px",
                                color: "#000000",
                            }}
                        >
                            <li style={{ fontSize: 20 }}>
                                👉 Founders who’ve been stuck in “just one more week” projects.

                            </li>
                            <li style={{ fontSize: 20 }}>
                                👉 Non-tech business owners tired of chasing status updates.


                            </li>
                            <li style={{ fontSize: 20 }}>
                                👉 Anyone planning a project and wants to make sure this time, it actually ships on time.

                            </li>
                        </ul>
                        <br />

                    </p>
                    <br />
                    <div className="text-center">
                        <button onClick={openModal} className="LM_link_btn">
                            Get My Free E-Book
                        </button>
                    </div>
                    <br /> <br />
                </div>
            </div>
        </div>
    </section>
);



const HowItWorks = ({ openModal }) => (
    <section
        className="py-5"
        style={{
            backgroundImage: `linear-gradient(to right, rgba(11, 29, 81, 0.7), rgba(11, 29, 81, 0.7)), url(${blueBack})`,
            backgroundSize: "100% 100%",
            backgroundRepeat: "no-repeat",
        }}
    >
        <div className="container">
            <h3 className="tc-primary head-font mb-4 text-center text-white">
                Why You’ll Love This Guide:

            </h3>

            <div className="text-center text-white">
                <p
                    className="mt-5"
                    style={{
                        fontFamily: "Inter",
                        fontStyle: "normal",
                        fontWeight: 400,
                        fontSize: "24px",
                        lineHeight: "36px",
                        color: "#000000",
                    }}
                >
                    <ul
                        className="text-white"
                        style={{
                            listStyle: "none",
                            fontFamily: "Inter",
                            fontStyle: "normal",
                            fontWeight: 400,
                            fontSize: "24px",
                            lineHeight: "36px",
                            color: "#000000",
                        }}
                    >
                        <li style={{ fontSize: 20 }}>
                            👉 Because it’s not a lecture, it’s a relief.It’s written for busy founders who want clarity, not complexity.
                        </li>
                        <li style={{ fontSize: 20 }}>
                            👉 You’ll see how a pre-project audit turns chaos into calm, with structure, visibility, and realistic timelines you can actually trust.

                        </li>

                    </ul>
                    <br />
                </p>
            </div>


            <div className="text-center">
                <button onClick={openModal} className="LM_link_btn_white w-auto">
                    Get My Free E-Book
                </button>
            </div>
        </div>
    </section>
);




const MiniStory = ({ openModal }) => (
    <section
        className="py-5"
        style={{
            backgroundImage: `linear-gradient(to right, rgba(11, 29, 81, 0.7), rgba(11, 29, 81, 0.7)), url(${blueBack})`,
            backgroundSize: "cover",
            backgroundRepeat: "no-repeat",
        }}
    >
        <div className="container">
            <div className="text-center text-white">
                <h2 className="tc-primary head-font mb-4 text-center text-white">
                    What Makes It Different:
                </h2>
                <p className="fs-20 mb-4 text-center text-white" style={{ maxWidth: "750px", margin: "0 auto 2rem" }}>
                    👉 Most “timeline tips” come after your project’s already late. This one shows you how to prevent delays before they even begin.

                </p>

                <p className="fs-20 mb-4 text-center text-white" style={{ maxWidth: "750px", margin: "0 auto 2rem" }}>
                    👉 You’ll never look at “expected delivery date” the same way again.


                </p>

                <div className="text-center">
                    <button onClick={openModal} className="LM_link_btn_white">
                        Get Your Free E-Book
                    </button>
                </div>
            </div>
        </div>
    </section>
);

// Final CTA Section Component
const FinalCtaSection = ({ offeringType, openModal }) => (
    <section
        className="py-5"
        style={{
            backgroundImage: `linear-gradient(to right, rgba(11, 29, 81, 0.7), rgba(11, 29, 81, 0.7)), url(${blueBack})`,
            backgroundSize: "cover",
            backgroundRepeat: "no-repeat",
        }}
    >
        <div className="container">
            <div className="text-center text-white">
                <h2 className="tc-primary head-font mb-4 text-center text-white">
                    Ready to Stay on Schedule?
                </h2>
                <p className="fs-20 mb-4 text-center text-white">

                    Pop in your name and email below — and I’ll send the guide straight to your inbox.
                    No spam. No sales talk. Just one founder-to-founder clarity drop.


                </p>

                <div className="text-center">
                    <button onClick={openModal} className="LM_link_btn_white">
                        Get Your Free E-Book
                    </button>
                </div>
            </div>
        </div>
    </section>
);

// Lead Capture Modal Component
const LeadCaptureModal = ({
    isOpen,
    closeModal,
    formData,
    handleInputChange,
    showError,
    isSubmitting,
    submitLeadForm,
    offeringType,
    downloadLink,
    handleManualDownload,
    isFormSubmitted,
    nicheTypes = [],
}) => (
    <Modal
        open={isOpen}
        onClose={closeModal}
        aria-labelledby="lead-capture-modal"
        aria-describedby="lead-capture-form"
    >
        <Box sx={modalStyle}>
            <div className="text-center mb-4">
                <h4 className="tc-primary head-font">
                    {offeringType === "E-Book"
                        ? "Get Your Free How an Audit Keeps Your Timeline on Track E-Book"
                        : "Register for Free Software Audit"}
                </h4>
                <p className="text-muted">
                    {offeringType === "E-Book"
                        ? "Enter your details below and we'll send the PDF guide directly to your inbox."
                        : "Enter your details below to reserve your spot in our exclusive Software Audit."}
                </p>
            </div>

         


            {(!isFormSubmitted || (offeringType === "E-Book")) && (
                <>
                    <div className="mb-3">
                        <TextField
                            fullWidth
                            label="Full Name"
                            name="name"
                            value={formData.name}
                            onChange={handleInputChange}
                            error={showError && !formData.name.trim()}
                            helperText={
                                showError && !formData.name.trim() ? "Name is required" : ""
                            }
                            margin="dense"
                            sx={{ "& input": { background: "#f8fafc" } }}
                            inputProps={{ maxLength: 100 }}
                        />
                    </div>
                    <div className="mb-4">
                        <TextField
                            fullWidth
                            label="Email Address"
                            name="email"
                            type="email"
                            value={formData.email}
                            onChange={handleInputChange}
                            error={showError && !formData.email.trim()}
                            helperText={
                                showError && !formData.email.trim()
                                    ? "Valid email is required"
                                    : ""
                            }
                            margin="dense"
                            sx={{ "& input": { background: "#f8fafc" } }}
                            inputProps={{ maxLength: 150 }}
                        />
                    </div>
                    <div className="mb-4">
                        <TextField
                            fullWidth
                            label="Phone Number"
                            name="phone"
                            type="text"
                            value={formData.phone}
                            onChange={handleInputChange}
                            error={showError && !formData.phone.trim()}
                            helperText={
                                showError && !formData.phone.trim()
                                    ? "Valid phone is required"
                                    : ""
                            }
                            margin="dense"
                            sx={{ "& input": { background: "#f8fafc" } }}
                            inputProps={{ maxLength: 10 }}
                        />
                    </div>

                    <div className="mb-4">
                        <Select
                            fullWidth
                            name="nicheType"
                            value={formData.nicheType}
                            onChange={handleInputChange}
                            displayEmpty
                            error={showError && !formData.nicheType}
                            sx={{
                                "& .MuiSelect-select": {
                                    background: "#e0e5ef",
                                    padding: "12px 14px",
                                },
                                "& .MuiOutlinedInput-root": {
                                    "& fieldset": {
                                        borderColor: "#e0e5ef",
                                    },
                                    "&:hover fieldset": {
                                        borderColor: "#b0b5bf",
                                    },
                                    "&.Mui-focused fieldset": {
                                        borderColor: "#3f51b5",
                                    },
                                },
                            }}
                            inputProps={{
                                "aria-label": "Industry Type",
                            }}
                        >
                            <MenuItem value="" disabled>
                                Industry Type
                            </MenuItem>
                            {nicheTypes?.map((type) => (
                                <MenuItem key={type} value={type}>
                                    {type}
                                </MenuItem>
                            ))}
                        </Select>

                        {showError && !formData.nicheType && (
                            <p
                                style={{
                                    color: "#d32f2f",
                                    fontSize: "0.75rem",
                                    margin: "3px 14px 0",
                                }}
                            >
                                Industry type is required
                            </p>
                        )}
                    </div>

                    {formData.nicheType === "Other" && (
                        <div className="mb-4">
                            <TextField
                                fullWidth
                                variant="outlined"
                                label="Specify Your Industry"
                                name="industry_other"
                                value={formData.industry_other}
                                onChange={handleInputChange}
                                error={
                                    showError &&
                                    formData.nicheType === "Other" &&
                                    !formData.industry_other.trim()
                                }
                                helperText={
                                    showError &&
                                        formData.nicheType === "Other" &&
                                        !formData.industry_other.trim()
                                        ? "Please specify your industry"
                                        : ""
                                }
                                required={formData.nicheType === "Other"}
                                margin="normal"
                                inputProps={{ maxLength: 100 }}
                                sx={{
                                    "& .MuiOutlinedInput-root": {
                                        "& input": {
                                            background: "#e0e5ef",
                                            padding: "12px 14px",
                                        },
                                    },
                                }}
                            />
                        </div>
                    )}
                    <div className="d-flex gap-2">
                        <button
                            onClick={closeModal}
                            className="btn btn-outline-secondary flex-fill"
                            disabled={isSubmitting}
                        >
                            Cancel
                        </button>
                        <button
                            onClick={submitLeadForm}
                            className="LM_link_btn1"
                            disabled={isSubmitting}
                            // style={{
                            //     background: "#1e3a8a",
                            //     border: "none",
                            //     position: "relative",
                            // }}
                        >
                            {isSubmitting ? (
                                <>
                                    <CircularProgress size={20} sx={{ color: "white", mr: 1 }} />
                                    Sending...
                                </>
                            ) : offeringType === "E-Book" ? (
                                "Get Your Free E-Book"
                            ) : (
                                "Register for Software Audit"
                            )}
                        </button>
                    </div>
                    <p className="text-center text-muted small mt-3 mb-0">
                        We respect your privacy. Unsubscribe at any time.
                    </p>
                </>
            )}
        </Box>
    </Modal>
);

const BonusSection = ({ openModal }) => (
    <section className="py-5" style={{ backgroundColor: "#f8fafc" }}>
        <div className="container">
            <div className="text-center">
                <h2 className="tc-primary head-font mb-4">Bonus for You:</h2>
                <p className="fs-20 text-muted mb-4" style={{ maxWidth: "800px", margin: "0 auto 2rem" }}>
                    Once you’ve read it, you’ll get a free invite to our Software Audit Session —
                    where we’ll map your exact project timeline, identify hidden risks, and give you a milestone plan that actually works.

                </p>

                <p className="fs-20" style={{ fontWeight: "600", color: "#0a2458", marginBottom: "2rem" }}>
                    Because deadlines don’t fail — planning does.
                    <br />
                    And we’re here to fix that.



                </p>

                <button onClick={openModal} className="LM_link_btn">
                    Get Your Free E-Book
                </button>
            </div>
        </div>
    </section>
);

const TrustSection = () => (
    <section className="py-5" style={{ backgroundColor: "#fff" }}>
        <div className="container">
            <div className="row text-center justify-content-center">
                <div className="col-lg-10">
                    <div className="row g-6">
                        <div className="col-md-4">
                            <div className="p-3">
                                <p className="fs-20" style={{ fontWeight: "600", color: "#0a2458", marginBottom: "2rem" }}>
                                    ✅ Used by 200+ founders
                                </p>
                                <p className="text-muted mb-0">to bring delayed projects back on track.</p>
                            </div>
                        </div>
                        <div className="col-md-4">
                            <div className="p-3">
                                <p className="fs-20" style={{ fontWeight: "600", color: "#0a2458", marginBottom: "2rem" }}>
                                    🧩 Created by real developers
                                </p>
                                <p className="text-muted mb-0">not marketing folks.</p>
                            </div>
                        </div>
                        <div className="col-md-4">
                            <div className="p-3">
                                <p className="fs-20" style={{ fontWeight: "600", color: "#0a2458", marginBottom: "2rem" }}>
                                    🔐 100% privacy
                                </p>
                                <p className="text-muted mb-0">Zero spam. Just clarity.</p>
                            </div>
                        </div>

                    </div>
                </div>
            </div>
        </div>
    </section>
);


const TestimonialSection = () => (
    <section
        className="py-75"
        style={{ backgroundColor: "#fff" }}
    >
        <div className="container">
            <div
                className="row justify-content-start"
                style={{ paddingTop: "50px", paddingLeft: "30px" }}
            >
                <div className="col-lg-10">
                    <div className="text-start mb-5">
                        <h3 className="tc-primary head-font mb-4" style={{
                            fontWeight: "bold",
                            fontStyle: "italic",

                        }}>
                            "We cut 3 weeks of wasted time just by auditing our plan first!"
                        </h3>
                       <p
                            className="fs-20 mb-4"
                            style={{ maxWidth: "1045px", margin: "0 auto 2rem" }}
                        >
                            — Priya, SaaS Founder

                        </p>
                    </div>
                </div>
            </div>
        </div>
    </section>
);

const EbookTimeLineLeadMagnet = () => {
    const [formData, setFormData] = useState({
        name: "",
        email: "",
        phone: "",
        nicheType: "",
        industry_other: "",
    });
    const [showError, setShowError] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [downloadLink, setDownloadLink] = useState("");
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isFormSubmitted, setIsFormSubmitted] = useState(false);
    const { slug: paramSlug } = useParams();
    const location = useLocation();

    const nicheTypes = [
        "Manufacturing & Industrial",
        "Technology & Software",
        "Professional Services",
        "Retail & E-commerce",
        "Construction & Real Estate",
        "Logistics & Supply Chain",
        "Energy & Utilities",
        "Healthcare & Life Sciences",
        "Education & Nonprofit",
        "Finance & Insurance",
        "Media, Entertainment & Hospitality",
        "Other",
    ];

    const [slug, setSlug] = useState(() => {
        const path = window.location.pathname;
        const match = path.match(/\/lm\/(.+)/);
        return match ? match[1] : "";
    });

    const fallbackSlugMap = {
        "/lm/how-an-audit-keeps-your-timeline-on-track": "how-an-audit-keeps-your-timeline-on-track",
    };

    // final slug = either paramSlug OR fallback
    const actualSlug = paramSlug || fallbackSlugMap[location.pathname] || "";

    // get UTM params from query string
    const searchParams = new URLSearchParams(location.search);
    const utmParams = {
        utm_source: searchParams.get("utm_source") || "direct",
        utm_medium: searchParams.get("utm_medium") || "email",
        utm_campaign: searchParams.get("utm_campaign") || "default",
    };


    const {
        apiData: leadMagnet,
        isLoading: isLeadMagnetLoding,
        error: leadMagnetError,
    } = useFetch(() => getLeadMagnet(actualSlug, utmParams), actualSlug);

    // const {
    //   apiData: leadMagnet,
    //   isLoading: isLeadMagnetLoding,
    //   error: leadMagnetError,
    // } = useFetch(getLeadMagnet, slug);

    useEffect(() => {
        if (leadMagnetError) {
            cogoToast.error("Failed to load content");
        }
        if (leadMagnet?.status === "error") {
            cogoToast.error("Failed to load content");
        }
    }, [leadMagnetError, leadMagnet]);

    useEffect(() => {
        document.title = "Software Audit - Plural Code Technologies";
    }, []);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const openModal = () => setIsModalOpen(true);

    const closeModal = () => {
        setIsModalOpen(false);
        setFormData({
            name: "",
            email: "",
            phone: "",
            nicheType: "",
            industry_other: "",
        });
        setShowError(false);
    };

    const submitLeadForm = async () => {
        const { name, email, phone, nicheType, industry_other } = formData;

        if (!name.trim() || !email.trim() || !phone.trim() || !nicheType) {
            setShowError(true);
            cogoToast.info("Please fill in all required fields");
            return;
        }

        if (nicheType === "Other" && !industry_other.trim()) {
            setShowError(true);
            cogoToast.info("Please specify your industry");
            return;
        }

        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(email)) {
            setShowError(true);
            cogoToast.error("Please enter a valid email address");
            return;
        }

        const phoneRegex = /^\d{10}$/;
        if (!phoneRegex.test(phone)) {
            setShowError(true);
            cogoToast.error("Phone number must be exactly 10 digits");
            return;
        }

        setIsSubmitting(true);

        try {
            const payload = {
                name,
                email,
                phone,
                slug: leadMagnet?.slug,
                nicheType,
                industry_other: nicheType === "Other" ? industry_other : "",
                offering_type: leadMagnet?.offering_type,
            };

            const response = await postLeadMagnetResponseForm(payload);

            if (response.status === "ok") {
                cogoToast.success(
                    "Thank you! Your E-Book will be sent to your email shortly."
                );
                setFormData({
                    name: "",
                    email: "",
                    phone: "",
                    nicheType: "",
                    industry_other: "",
                });
                setShowError(false);
                setIsFormSubmitted(true);
            } else {
                cogoToast.error(
                    response.message || "Something went wrong. Please try again."
                );
            }
        } catch (err) {
            console.error("Form submission error:", err);
            cogoToast.error("Something went wrong. Please try again.");
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleManualDownload = async () => {
        if (!downloadLink) {
            cogoToast.error("No download token available");
            return;
        }

        try {
            const downloadResult = await downloadLeadMagnet(slug, downloadLink);

            if (downloadResult.success) {
                // Create download link and trigger download
                const link = document.createElement("a");
                link.href = downloadResult.fileUrl;
                link.download = downloadResult.filename;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);

                cogoToast.success("Download started! Check your Downloads folder.");

                // Clear the download link since it's one-time use
                setDownloadLink("");

                // Clean up the blob URL after some time
                setTimeout(() => {
                    window.URL.revokeObjectURL(downloadResult.fileUrl);
                }, 30000); // 30 seconds
            } else {
                cogoToast.error(
                    downloadResult.error || "Download failed. Please contact our team."
                );
                // Clear the download link if it failed (likely used already)
                setDownloadLink("");
            }
        } catch (error) {
            console.error("Download error:", error);
            cogoToast.error("Download failed. Please try again.");
        }
    };

    if (isLeadMagnetLoding) {
        return (
            <div
                className="container text-center"
                style={{
                    minHeight: "100vh",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                }}
            >
                <CircularProgress />
            </div>
        );
    }

    if (leadMagnetError) {
        return (
            <div className="container text-center py-5">
                <h3>Error loading the page</h3>
                <p>{leadMagnetError.message || "Unknown error occurred"}</p>
            </div>
        );
    }

    return (
        <div>
            <Helmet>
                <meta name="title" content={leadMagnet?.meta_title} />
                <meta name="description" content={leadMagnet?.meta_description} />
                <meta
                    property="og:description"
                    content={leadMagnet?.meta_description}
                />
            </Helmet>
            <main className="wrapper">
                <HeroSection
                    content={{ title: leadMagnet?.title, subtitle: leadMagnet?.subtitle }}
                    offeringType={leadMagnet?.offering_type}
                    formData={formData}
                    handleInputChange={handleInputChange}
                    showError={showError}
                    isSubmitting={isSubmitting}
                    submitLeadForm={submitLeadForm}
                    downloadLink={downloadLink}
                    handleManualDownload={handleManualDownload}
                    isFormSubmitted={isFormSubmitted}
                    nicheTypes={nicheTypes}
                />

                <ThingsYouWillGetInThisReport openModal={openModal} />

                <HowItWorks openModal={openModal} />

                <WhoIsThisFor openModal={openModal} />



                <FinalCtaSection
                    offeringType={leadMagnet?.offering_type}
                    openModal={openModal}
                />

                <BonusSection openModal={openModal} />


                <TrustSection />

                <MiniStory openModal={openModal} />
                <TestimonialSection />
                <LeadCaptureModal
                    isOpen={isModalOpen}
                    closeModal={closeModal}
                    formData={formData}
                    handleInputChange={handleInputChange}
                    showError={showError}
                    isSubmitting={isSubmitting}
                    submitLeadForm={submitLeadForm}
                    downloadLink={downloadLink}
                    handleManualDownload={handleManualDownload}
                    isFormSubmitted={isFormSubmitted}
                    offeringType={leadMagnet?.offering_type}
                    nicheTypes={nicheTypes}
                />

            </main>
        </div>
    );
};

export default EbookTimeLineLeadMagnet;
